// "This artwork's collection membership just changed."
//
// The add-to-collection modal is opened on top of whatever asked for it and closes itself
// on save, so the opener never learns the outcome — the two are separate entries in the
// modal stack with no return channel. This is that channel.
//
// A typed subscriber list rather than a `window` CustomEvent, for the same reason as
// features/incomingReview.ts: both ends import one symbol, so `tsc` checks the payload and
// there is no event-name string to mistype into silence.

type MembershipListener = (artworkId: number) => void;

const listeners = new Set<MembershipListener>();

/** Subscribe. Returns the unsubscribe, so a `useEffect` can hand it straight back. */
export function onArtworkCollectionsChanged(fn: MembershipListener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Announce a change. Called after the write succeeds, never before. */
export function artworkCollectionsChanged(artworkId: number): void {
  for (const fn of [...listeners]) fn(artworkId);
}

type CreatedListener = (collectionId: string) => void;

const createdListeners = new Set<CreatedListener>();

/**
 * Subscribe to "a new list was created".
 *
 * The create form (`editlist:new`) opens ON TOP of the picker that offered it, and closes
 * itself on save — so the picker underneath never learns that its list of lists is now out
 * of date. It showed a stale set with the new list missing, which reads as "it wasn't
 * saved" even though the row exists.
 */
export function onListCreated(fn: CreatedListener): () => void {
  createdListeners.add(fn);
  return () => {
    createdListeners.delete(fn);
  };
}

/** Announce a newly created list. Called after the write succeeds, never before. */
export function listCreated(collectionId: string): void {
  for (const fn of [...createdListeners]) fn(collectionId);
}
